import {
  Avatar,
  Button,
  Flex,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  useColorMode,
  useColorModeValue,
} from '@chakra-ui/react';
import PropTypes from 'prop-types';
import React, { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { IoMdMoon, IoMdSunny } from 'react-icons/io';
import { IoMenuOutline } from 'react-icons/io5';
import { logOut } from 'services/auth';
import { useFirebaseAuth } from 'domains/auth/hooks/useFirebaseAuth';
import { getUserByFirebaseUid } from 'api/api';
import { SidebarContext } from 'contexts/SidebarContext';
import LanguageSelector from 'components/i18n/LanguageSelector';
import MessageNotificationBell from 'components/navbar/MessageNotificationBell';
import NotificationBell from 'components/navbar/NotificationBell';
import { useTranslation } from 'i18n/TranslationProvider';

export default function HeaderLinks(props) {
  const { secondary } = props;
  const { colorMode, toggleColorMode } = useColorMode();
  const { uid, loading: authLoading } = useFirebaseAuth();
  const [user, setUser] = useState(null);
  const { openMobileSidebar } = useContext(SidebarContext) || {};
  const { t } = useTranslation();

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (authLoading) return;
      if (!uid) {
        setUser(null);
        return;
      }
      try {
        const u = await getUserByFirebaseUid(uid);
        if (!cancelled) setUser(u);
      } catch (e) {
        console.error('Error fetching user profile:', e);
        if (!cancelled) setUser(null);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [uid, authLoading]);

  // Chakra Color Mode values
  const menuBg = useColorModeValue('white', 'navy.800');
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('#E6ECFA', 'rgba(135, 140, 189, 0.3)');
  const shadow = useColorModeValue(
    '14px 17px 40px 4px rgba(112, 144, 176, 0.18)',
    '14px 17px 40px 4px rgba(112, 144, 176, 0.06)'
  );

  const handleLogout = () => {
    logOut();
    window.location.href = '/auth/sign-in';
  };

  return (
    <Flex
      w={{ sm: '100%', md: 'auto' }}
      alignItems="center"
      flexDirection="row"
      bg={menuBg}
      flexWrap={secondary ? { base: 'wrap', md: 'nowrap' } : 'unset'}
      p="10px"
      borderRadius="30px"
      boxShadow={shadow}
      gap={3}
    >
      <Button variant="no-hover" bg="transparent" onClick={toggleColorMode} display={'none'}>
        <Icon
          as={colorMode === 'light' ? IoMdMoon : IoMdSunny}
          w="18px"
          h="18px"
        />
      </Button>

      <Menu>
        <MenuButton p="0px">
          <Avatar
            size="sm"
            w="40px"
            h="40px"
            name={user?.displayName || user?.email || 'U'}
            bg="#11047A"
            color="white"
          />
        </MenuButton>
        <MenuList
          boxShadow={shadow}
          p="0px"
          mt="10px"
          borderRadius="20px"
          bg={menuBg}
          border="none"
        >
          <Flex w="100%" mb="0px">
            <Text
              ps="20px"
              pt="16px"
              pb="10px"
              w="100%"
              borderBottom="1px solid"
              borderColor={borderColor}
              fontSize="sm"
              fontWeight="700"
              color={textColor}
            >
              👋&nbsp; {t('Hello')},{' '}
              {authLoading
                ? t('Loading…')
                : user?.displayName || user?.email || t('friend')}
            </Text>
          </Flex>
          <Flex flexDirection="column" p="10px">
            <MenuItem
              as={Link}
              to="/profile"
              _hover={{ bg: 'blue.50', color: 'blue.500', transition: '0.2s' }}
              _focus={{ bg: 'blue.50', color: 'blue.500' }}
              borderRadius="8px"
              px="14px"
            >
              <Text fontSize="sm">{t('My Profile')}</Text>
            </MenuItem>
            <MenuItem
              _hover={{ bg: 'red.50', color: 'red.500', transition: '0.2s' }}
              _focus={{ bg: 'red.50', color: 'red.500' }}
              color="red.400"
              borderRadius="8px"
              px="14px"
              onClick={handleLogout}
            >
              <Text fontSize="sm">{t('Log out')}</Text>
            </MenuItem>
          </Flex>
        </MenuList>
      </Menu>
      <MessageNotificationBell />
      <NotificationBell />
      <LanguageSelector variant="ghost" size="sm" />
      {openMobileSidebar && (
        <IconButton
          aria-label={t('Open menu')}
          icon={<IoMenuOutline />}
          onClick={openMobileSidebar}
          variant="ghost"
          color="gray.500"
          display={{ base: 'inline-flex', xl: 'none' }}
          ml="auto"
          _hover={{ bg: 'gray.100' }}
          _active={{ bg: 'gray.200' }}
        />
      )}
    </Flex>
  );
}

HeaderLinks.propTypes = {
  variant: PropTypes.string,
  fixed: PropTypes.bool,
  secondary: PropTypes.bool,
  onOpen: PropTypes.func,
};
